import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { onboardingPath } from "../../core/primitives/onboarding";
import { isWaitingOnProvider, providerMessage } from "../../core/primitives/provider-status";
import { SetupChecklistActions, SetupChecklistState } from "./setup-checklist-model";

type ChecklistItem = { key: string; title: string; body: string; action: string; to: string; canCheck: boolean };

/**
 * Stripe is the one onboarding step a merchant may skip, so it has to keep
 * asking: quietly, on the dashboard, instead of as a modal that blocks work.
 * The shell owns its `SetupChecklistController` and shows it above every page but Settings.
 */
export function SetupChecklist() {
  const state = useSubtree(SetupChecklistState);
  const actions = useSubtree(SetupChecklistActions);
  const observer = useObserver();
  const store = observer.watch(state.store);
  const stripe = observer.watch(state.stripe);
  const known = observer.watch(state.stripeKnown);
  const checking = observer.watch(state.checking);
  const checkError = observer.watch(state.checkError);

  if (!store || !known) return null;

  const items: ChecklistItem[] = [];

  // Locations are not listed here: the store gate sends a shop with none back
  // to step 2, so reaching this page already means the shop has an address.
  if (stripe?.status !== "ACTIVE") {
    // Nothing here is the merchant's move while Stripe is verifying, so this
    // stops asking them to finish something and only offers the news.
    const waiting = isWaitingOnProvider(stripe);
    items.push({
      key: "stripe",
      title: waiting
        ? "Stripe is reviewing your details"
        : stripe?.status === "ERROR"
          ? "Stripe needs attention"
          : "Finish connecting Stripe to take payments",
      body:
        providerMessage(stripe, "Stripe") ??
        (stripe?.status === "PENDING"
          ? "Stripe still has outstanding requirements for this account."
          : "You can scan and price cards without it, but you can't charge for them."),
      action: waiting
        ? "View status"
        : stripe?.status === "PENDING" || stripe?.status === "ERROR"
          ? "Continue onboarding"
          : "Connect Stripe",
      to: onboardingPath("payments"),
      // Only once Stripe has something on file: before that there is nothing to ask it about.
      canCheck: stripe != null,
    });
  }

  if (items.length === 0) return null;

  return (
    // Amber, not the accent: "unfinished" is exactly the state the mint must not claim.
    <section className="note note-warn mb-6 p-5">
      <h2 className="text-sm font-semibold text-warn">Finish setting up</h2>
      <ul className="mt-3 flex flex-col gap-2.5">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex flex-col gap-2.5 rounded-row border border-line bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-white">{item.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted">{item.body}</p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-auto">
              {item.canCheck && (
                <button
                  type="button"
                  onClick={() => void actions.checkAgain()}
                  disabled={checking}
                  className="btn-secondary btn-xs"
                >
                  {checking ? "Checking with Stripe…" : "Check again"}
                </button>
              )}
              <Link to={item.to} className="btn-primary btn-xs">
                {item.action}
              </Link>
            </div>
          </li>
        ))}
      </ul>
      {checkError && <p className="mt-3 text-xs text-danger">{checkError}</p>}
    </section>
  );
}
